import { useEffect, useState } from 'react';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useFund } from '../context/FundContext';
import FundDateBar from '../components/FundDateBar';
import { Loading } from '../components/Loader';
import { api } from '../api';
import { money, shortName } from '../format';

const COLORS = ['#3fb6c9', '#5b6ee1', '#bb6bd9', '#4caf7d', '#f2994a', '#e57373',
  '#9575cd', '#90a4ae', '#26a69a', '#7e57c2', '#ffa726', '#66bb6a'];

// Top N slices + an aggregated "Others" bucket, so a 48-account breakdown stays
// readable as a donut.
function topWithOthers(list, key, n = 8) {
  const top = list.slice(0, n);
  const rest = list.slice(n);
  if (rest.length) {
    top.push({ [key]: 'Others', mv: rest.reduce((a, x) => a + x.mv, 0) });
  }
  return top;
}

function Card({ label, value, big, tone = 'blue' }) {
  return (
    <div className={`pf-card ${tone} ${big ? 'big' : ''}`}>
      <div className="pf-card-label">{label}</div>
      <div className="pf-card-value">{value}</div>
    </div>
  );
}

function Donut({ title, data, nameKey, subtitle }) {
  return (
    <div className="panel">
      <h3>{title}</h3>
      {subtitle && <div className="muted-inline">{subtitle}</div>}
      {data.length ? (
        <div className="holdings-wrap">
          <ResponsiveContainer width={230} height={230}>
            <PieChart>
              <Pie data={data} dataKey="mv" nameKey={nameKey} innerRadius={58} outerRadius={100}>
                {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => money(v)} />
            </PieChart>
          </ResponsiveContainer>
          <div className="holdings-legend pf-legend">
            {data.map((d, i) => (
              <div className="legend-row" key={d[nameKey]}>
                <span className="legend-dot" style={{ background: COLORS[i % COLORS.length] }} />
                <span className="legend-name">{shortName(String(d[nameKey]))}</span>
                <span className="legend-val">{money(d.mv)}</span>
              </div>
            ))}
          </div>
        </div>
      ) : <p className="muted">No positions for this date.</p>}
    </div>
  );
}

export default function Portfolio() {
  const { fund, date } = useFund();
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!fund || !date) return undefined;
    let active = true;
    setLoading(true);
    setError(null);
    api.portfolio(fund, date)
      .then((data) => { if (active) { setD(data); setError(null); } })
      .catch((e) => { if (active) { setError(e.message); setD(null); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [fund, date]);

  const c = d?.cards;

  return (
    <div>
      <FundDateBar />
      {error && <div className="error">Error: {error}</div>}
      {loading && <Loading label="Loading portfolio…" />}

      {d && !loading && (
        <>
          {d.snapshotDate && d.snapshotDate !== date && (
            <div className="notice">
              Showing the latest available positions as of {d.snapshotDate}.
            </div>
          )}

          <div className="pf-grid">
            <Card big label="Capital (USD)" value={money(c.capital)} />
            <Card big label="Cash (USD)" value={money(c.cash)} />
            <Card big label="Portfolio (USD)" value={money(c.portfolio)} />
            <div className="pf-fees">
              <Card label="Management Fee (USD)" value={money(c.managementFee)} tone="plain" />
              <Card label="Performance Fee (USD)" value={money(c.performanceFee)} tone="plain" />
            </div>
          </div>

          <div className="pf-grid pf-grid-sm">
            <Card label="Net Income (USD)" value={money(c.netIncome)} tone="plain" />
            <Card label="VAMI (USD)" value={money(c.vami)} tone="plain" />
            <Card label="Realized G/L (USD)" value={money(c.realizedGL)} tone="plain" />
            <Card label="Unrealized G/L (USD)" value={money(c.unrealizedGL)} tone="plain" />
          </div>

          <div className="bottom-grid pf-charts">
            <Donut
              title="Source Breakdown"
              subtitle="Market value by custodian / broker account"
              data={topWithOthers(d.sourceBreakdown, 'source')}
              nameKey="source"
            />
            <Donut
              title="Asset Allocation"
              subtitle="Market value by asset class"
              data={d.assetAllocation}
              nameKey="assetClass"
            />
          </div>

          <div className="panel">
            <h3>Holdings {d.snapshotDate && <span className="muted-inline">as of {d.snapshotDate}</span>}</h3>
            {d.holdings.length ? (
              <table className="table">
                <thead>
                  <tr>
                    <th>Security</th><th>Asset Class</th>
                    <th className="num">Market Value (USD)</th>
                    <th className="num">Unrealized G/L (USD)</th>
                    <th className="num">% of Book</th>
                  </tr>
                </thead>
                <tbody>
                  {d.holdings.slice(0, 50).map((h) => (
                    <tr key={h.security}>
                      <td>{shortName(h.security)}</td>
                      <td>{h.assetClass}</td>
                      <td className="num">{money(h.mv)}</td>
                      <td className={`num ${h.gl >= 0 ? 'gain' : 'loss'}`}>{money(h.gl)}</td>
                      <td className="num">{h.pct.toFixed(2)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p className="muted">No positions for this date.</p>}
          </div>
        </>
      )}
    </div>
  );
}
